import { useState, useEffect } from 'react';
import { colors } from '../../styles/colors';
import { useNavigate } from 'react-router-dom';
import { formatPatientName } from '../../utils/nameUtils';

import { 
  Box, 
  TextField, 
  IconButton, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Paper,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { EligibilityDataResponse, EligibilityFilter, getEligibilityData, toggleFavoriteStatus } from '../../services/eligibilityService';
import { DataProvider, getDataProviders } from '../../services/dataProviderService';
import { formatDateForDisplay, formatDateForApi } from '../../utils/dateUtils';

interface EligibilityTableProps {
  initialFilter?: {
    activeStatus?: string;
    groupId?: string;
    planCode?: string;
    effectiveDateStart?: string;
    effectiveDateEnd?: string;
    dataProviderId?: number;
  };
}

const EligibilityTable = ({ initialFilter }: EligibilityTableProps) => {
  const navigate = useNavigate();
  const [eligibilityData, setEligibilityData] = useState<EligibilityDataResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dataProviders, setDataProviders] = useState<DataProvider[]>([]);
  const [feedback, setFeedback] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [firstNameFilter, setFirstNameFilter] = useState('');
  const [lastNameFilter, setLastNameFilter] = useState('');
  const [ssnFilter, setSsnFilter] = useState('');
  const [memberIdFilter, setMemberIdFilter] = useState('');
  const [dateOfBirthFilter, setDateOfBirthFilter] = useState<dayjs.Dayjs | null>(null);
  const [filter, setFilter] = useState<EligibilityFilter>(() => {
    if (initialFilter) {
      return {
        favoritesOnTop: true,
        ...initialFilter,
      };
    }
    return {
      favoritesOnTop: true
    };
  });

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'updatedAt',
      sort: 'desc',
    },
  ]);

  const handleToggleFavoriteStatus = async (eligibilityRecord: EligibilityDataResponse) => {
    try {
      await toggleFavoriteStatus(eligibilityRecord.id);
      await fetchEligibilityData();
      setFeedback({
        open: true,
        message: `${eligibilityRecord.isFavorite ? 'Removed from' : 'Added to'} favorites`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to toggle favorite status:', error);
      setFeedback({
        open: true,
        message: 'Failed to toggle favorite status. Please try again.',
        severity: 'error'
      });
    }
  }

  const handleCopyRecord = async (eligibilityRecord: EligibilityDataResponse) => {
    try {
      
      // Format the record details
      const fullName = formatPatientName(
        eligibilityRecord.firstName, 
        eligibilityRecord.lastName, 
        eligibilityRecord.middleName
      );
      
      // Format SSN for display
      const formattedSSN = eligibilityRecord.ssn && eligibilityRecord.ssn.length === 9 
        ? `${eligibilityRecord.ssn.slice(0, 3)}-${eligibilityRecord.ssn.slice(3, 5)}-${eligibilityRecord.ssn.slice(5)}`
        : eligibilityRecord.ssn || '';
      
      // Format address
      const fullAddress = [
        eligibilityRecord.streetAddress1,
        eligibilityRecord.streetAddress2,
        eligibilityRecord.city,
        eligibilityRecord.state,
        eligibilityRecord.zip
      ].filter(Boolean).join(', ');
      
      const recordDetails = `
ELIGIBILITY RECORD DETAILS
Name: ${fullName}
Member ID: ${eligibilityRecord.memberId}
Date of Birth: ${formatDateForDisplay(eligibilityRecord.dateOfBirth)}
SSN: ${formattedSSN}
Gender: ${eligibilityRecord.gender}

Coverage Information:
- Plan Code/Group ID: ${eligibilityRecord.planCode || 'N/A'}
- Coverage Tier: ${eligibilityRecord.coverageTier || 'N/A'}
- Relationship Code: ${eligibilityRecord.relationshipCode}
- Active Status: ${eligibilityRecord.activeStatus || 'N/A'}
- Primary Subscriber: ${eligibilityRecord.primarySubscriber ? 'Yes' : 'No'}

Effective Dates:
- Effective Date: ${formatDateForDisplay(eligibilityRecord.effectiveDate)}
- End Date: ${eligibilityRecord.endDate ? formatDateForDisplay(eligibilityRecord.endDate) : 'Active'}

Contact Information:
- Email: ${eligibilityRecord.email || 'N/A'}
- Home Phone: ${eligibilityRecord.homePhone || 'N/A'}
- Work Phone: ${eligibilityRecord.workPhone || 'N/A'}
- Mobile Phone: ${eligibilityRecord.mobilePhone || 'N/A'}
- Address: ${fullAddress || 'N/A'}
`.trim();

      // Copy to clipboard
      await navigator.clipboard.writeText(recordDetails);
      
      setFeedback({
        open: true,
        message: 'Record details copied to clipboard',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to copy record details:', error);
      setFeedback({
        open: true,
        message: 'Failed to copy record details. Please try again.',
        severity: 'error'
      });
    }
  }

  const columns: GridColDef[] = [
    { 
      field: "actions", 
      headerName: 'Actions', 
      width: 130,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<EligibilityDataResponse>) => {
        return (
          <Box>
            <IconButton 
              onClick={() => handleToggleFavoriteStatus(params.row)}
              size="small"
              title="Toggle Favorite"
            >
              {params.row.isFavorite ? <StarIcon sx={{ color: colors.warning }} /> : <StarBorderIcon />}
            </IconButton>
            <IconButton 
              onClick={() => handleCopyRecord(params.row)}
              size="small"
              title="Copy Record Details"
            >
              <ContentCopyIcon />
            </IconButton>
            <IconButton 
              onClick={() => handleEditNavigation(params.row.id)}
              size="small"
              title="Edit Eligibility Record"
            >
              <EditIcon />
            </IconButton>
          </Box>
        )
      }
    },
    { 
      field: 'memberId', 
      headerName: 'Member ID', 
      width: 150,
      disableColumnMenu: true,
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <div className="MuiDataGrid-columnHeaderTitle css-1gqmilo-MuiDataGrid-columnHeaderTitle">Member ID</div>
          <Tooltip title="MemberId is called 'FamilyId' in Four Winds data and 'IdNumber' in MedOne data">
            <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </Tooltip>
        </Box>
      )
    },
    { 
      field: 'patientName', 
      headerName: 'Patient Name', 
      width: 200,
      disableColumnMenu: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams<EligibilityDataResponse>) => {
        const { firstName, lastName, middleName } = params.row;
        return formatPatientName(firstName, lastName, middleName);
      }
    },
    { 
      field: 'dateOfBirth', 
      headerName: 'Date of Birth', 
      width: 130,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<EligibilityDataResponse>) => formatDateForDisplay(params.row.dateOfBirth)
    },
    { 
      field: 'ssn', 
      headerName: 'SSN', 
      width: 120,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<EligibilityDataResponse>) => {
        // Display full SSN since users are trusted
        const ssn = params.row.ssn;
        if (ssn && ssn.length === 9) {
          // Format standard 9-digit SSN with dashes
          return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5)}`;
        }
        // Display without dashes for non-standard lengths
        return ssn || '';
      }
    },
    { field: 'gender', headerName: 'Gender', width: 90, disableColumnMenu: true, sortable: false,  },
    { 
      field: 'effectiveDate', 
      headerName: 'Effective Date', 
      width: 130,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<EligibilityDataResponse>) => formatDateForDisplay(params.row.effectiveDate)
    },
    { 
      field: 'endDate', 
      headerName: 'End Date', 
      width: 130,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<EligibilityDataResponse>) => 
        params.row.endDate ? formatDateForDisplay(params.row.endDate) : 'Active'
    },
    { 
      field: 'planCode', 
      headerName: 'Plan Code', 
      width: 150,
      disableColumnMenu: true,
      sortable: false,
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <div className="MuiDataGrid-columnHeaderTitle css-1gqmilo-MuiDataGrid-columnHeaderTitle">Group ID</div>
          <Tooltip title="Plan Code is called 'GroupNum' in MedOne data and 'Department' in Four Winds data">
            <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </Tooltip>
        </Box>
      )
    },
    { 
      field: 'streetAddress1', 
      headerName: 'Street Address', 
      width: 200,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<EligibilityDataResponse>) => {
        const address = params.row.streetAddress1;
        const address2 = params.row.streetAddress2;
        return address2 ? `${address}, ${address2}` : address;
      }
    },
    { 
      field: 'relationshipCode', 
      headerName: 'Relationship Code', 
      width: 140, 
      disableColumnMenu: true,
      sortable: false,
    },
    { 
      field: 'coverageTier', 
      headerName: 'Coverage Tier', 
      width: 120, 
      disableColumnMenu: true,
      sortable: false, 
    },
    { 
      field: 'updatedAt', 
      headerName: 'Updated Date', 
      width: 130,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<EligibilityDataResponse>) => 
        params.row.updatedAt ? formatDateForDisplay(params.row.updatedAt, 'MMM DD, YYYY') : 'N/A'
    },
    { 
      field: 'dataProviderName', 
      headerName: 'Partner', 
      width: 130,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<EligibilityDataResponse>) => {
        // Find the data provider name from the dataProviders array
        const dataProvider = dataProviders.find(dp => dp.id === params.row.dataProviderId);
        return dataProvider?.name || 'Unknown';
      }
    },
  ];

  useEffect(() => {
    fetchEligibilityData();
    fetchDataProviders();
  }, []);

  useEffect(() => {
    fetchEligibilityData();
  }, [filter, paginationModel, sortModel]);

  // Debounce text filters to avoid excessive API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchEligibilityData();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [firstNameFilter, lastNameFilter, ssnFilter, memberIdFilter]);

  // Separate effect for date of birth filter with validation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Only trigger API call if we have a valid, complete date
      if (dateOfBirthFilter && dateOfBirthFilter.isValid()) {
        fetchEligibilityData();
      } else if (dateOfBirthFilter === null) {
        // If date is cleared (null), also trigger API call
        fetchEligibilityData();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [dateOfBirthFilter]);

  const fetchEligibilityData = async () => {
    try {
      setLoading(true);
      
      // Build complete filter with pagination and sorting
      const completeFilter: EligibilityFilter = {
        ...filter,
        firstName: firstNameFilter || undefined,
        lastName: lastNameFilter || undefined,
        ssn: ssnFilter || undefined,
        memberId: memberIdFilter || undefined,
        dateOfBirth: dateOfBirthFilter ? formatDateForApi(dateOfBirthFilter.format('YYYY-MM-DD')) : undefined,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        sortField: sortModel[0]?.field || 'updatedAt',
        sortDirection: sortModel[0]?.sort || 'desc',
      };
      
      const response = await getEligibilityData(completeFilter);
      setEligibilityData(response.data);
      setTotalCount(response.totalCount);
    } catch (error) {
      console.error('Failed to fetch eligibility data:', error);
      setFeedback({
        open: true,
        message: 'Failed to fetch eligibility data. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDataProviders = async () => {
    try {
      const providers = await getDataProviders();
      setDataProviders(providers);
    } catch (error) {
      console.error('Failed to fetch data providers:', error);
    }
  };

  const handleFilterChange = (field: keyof EligibilityFilter, value: string | boolean | number | null) => {
    setFilter(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  const clearFilters = () => {
    setFirstNameFilter('');
    setLastNameFilter('');
    setSsnFilter('');
    setMemberIdFilter('');
    setDateOfBirthFilter(null);
    setFilter({
      favoritesOnTop: true
    });
  };

  const handleCloseFeedback = () => {
    setFeedback(prev => ({ ...prev, open: false }));
  };

  const handleEditNavigation = (id: number) => {
    navigate(`/eligibility/${id}`)
  }

  return (
    <Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>Active Status</InputLabel>
            <Select
              size="small"
              value={filter.activeStatus || ''}
              label="Active Status"
              onChange={(e) => handleFilterChange('activeStatus', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
              <MenuItem value="Terminated">Terminated</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 130 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>Partner</InputLabel>
            <Select
              size="small"
              value={filter.dataProviderId || ''}
              label="Partner"
              onChange={(e) => handleFilterChange('dataProviderId', e.target.value ? Number(e.target.value) : null)}
            >
              <MenuItem value="">All</MenuItem>
              {dataProviders.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 130 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>First Name</InputLabel>
            <TextField
              size="small"
              value={firstNameFilter}
              onChange={(e) => setFirstNameFilter(e.target.value)}
              placeholder="Filter by first name"
            />
          </FormControl>

          <FormControl sx={{ minWidth: 130 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>Last Name</InputLabel>
            <TextField
              size="small"
              value={lastNameFilter}
              onChange={(e) => setLastNameFilter(e.target.value)}
              placeholder="Filter by last name"
            />
          </FormControl>

          <FormControl sx={{ minWidth: 130 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>Date of Birth</InputLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateOfBirthFilter}
                onChange={(newValue) => setDateOfBirthFilter(newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    placeholder: "Select date of birth"
                  }
                }}
              />
            </LocalizationProvider>
          </FormControl>

          <FormControl sx={{ minWidth: 130 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>SSN</InputLabel>
            <TextField
              size="small"
              value={ssnFilter}
              onChange={(e) => setSsnFilter(e.target.value)}
              placeholder="Full or partial SSN"
            />
          </FormControl>

          <FormControl sx={{ minWidth: 130 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 0 }}>Member ID</InputLabel>
              <Tooltip title="MemberId is called 'FamilyId' in Four Winds data and 'IdNumber' in MedOne data">
                <IconButton size="small" sx={{ p: 0.25, ml: 0.5 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              size="small"
              value={memberIdFilter}
              onChange={(e) => setMemberIdFilter(e.target.value)}
              placeholder="Filter by member ID"
            />
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={filter.favoritesOnly || false}
                onChange={(e) => handleFilterChange('favoritesOnly', e.target.checked)}
                size="small"
              />
            }
            label="Favorites Only"
            sx={{ mt: 3 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={filter.favoritesOnTop || false}
                onChange={(e) => handleFilterChange('favoritesOnTop', e.target.checked)}
                size="small"
              />
            }
            label="Favorites on Top"
            sx={{ mt: 3 }}
          />

          <IconButton 
            onClick={clearFilters}
            size="small"
            title="Clear Filters"
            sx={{mt:4}}
          >
            <FilterAltOffIcon />
          </IconButton>
        </Box>

        <DataGrid
        rows={eligibilityData}
        columns={columns}
        loading={loading}
        rowCount={totalCount}
        paginationMode="server"
        sortingMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        pageSizeOptions={[10, 25, 50, 100]}
        autoHeight
        sx={{ cursor: 'pointer' }}
      />
      </Paper>
      <Snackbar 
        open={feedback.open} 
        autoHideDuration={6000} 
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseFeedback} 
          severity={feedback.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EligibilityTable;
